import axios from 'axios';
import { useState, useEffect, useContext } from 'react';
import { useForm } from 'react-hook-form';
import { debounce } from 'lodash';

import { MyContext } from '../../Store';
import {
  trans,
  hash,
  localStorageItemName,
  replaceWithAsterisk,
  replaceAllWithAsterisk,
  replaceMailWithAsterisk,
  requestIsJaguar,
  getCookie,
  getUrlSearchParams,
} from '../../utils';
import { Modal } from './Modal';

const STATUS_ENUM = {
  form: 0,
  pdfLoading: 1,
  pdfAccepting: 2,
  orderLoading: 3,
  orderSuccess: 4,
  orderError: 5,
  addToCartPdfLoading: 6,
  addToCartPdfAccepting: 7,
  addToCartLoading: 8,
};

const urlParams = getUrlSearchParams();
const productID = urlParams.get('product');

export const OrderModal = () => {
  const [state, dispatch, saveData] = useContext(MyContext);
  const minimalOrder = state.productData.minimal_order;

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm();

  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [orderData, setOrderData] = useState({});
  const [currentStatus, setCurrentStatus] = useState(STATUS_ENUM.form);
  const [isPdfDownloaded, setIsPdfDownloaded] = useState(false);
  const [isCountriesLoaded, setIsCountriesLoaded] = useState(false);
  const [countries, setCountries] = useState([]);
  const [quantity, setQuantity] = useState(minimalOrder || 1);
  const [userData, setUserData] = useState({});

  const submitForm = async data => {
    setCurrentStatus(STATUS_ENUM.pdfLoading);

    const orderFormData = {
      invoice_first_name: data.firstName,
      invoice_last_name: data.lastName,
      invoice_address: data.address,
      invoice_address_house_number: data.addressHouseNumber,
      invoice_address_house_number2: data.addressHouseNumber2,
      invoice_postal_code: data.postalCode,
      invoice_city: data.city,
      invoice_country_code: data.country,
      invoice_province: data.province,
      invoice_name: data.companyName,
      invoice_tax_number: data.taxNumber,
      email: data.email,
      phone: data.phoneNumber,
      hash: hash(),
      count: quantity,
    };
    if (orderFormData.hash === null) {
      setCurrentStatus(STATUS_ENUM.orderError);
      console.error('Cannot get hash value');
      return;
    }

    try {
      const response = await axios.post('/api/configurator_create_order/', orderFormData, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.data) {
        getPdfData(orderFormData.hash);
        setOrderData(response.data);
      } else {
        setCurrentStatus(STATUS_ENUM.orderError);
        console.error('Cannot get order ID');
      }
    } catch (error) {
      setCurrentStatus(STATUS_ENUM.orderError);
      console.error(error);
    }
  };

  const redirectToCart = () => {
    const targetLocation =
      process.env.NODE_ENV === 'development' ? 'http://localhost:8000/cart' : 'https://jaguargift.com/cart';

    if (window.parent && window.parent !== window) {
      window.parent.location.href = targetLocation;
    } else {
      window.location.href = targetLocation;
    }
  };

  const getCountriesList = async () => {
    try {
      const response = await axios.get('/api/countries/?limit=300');
      if (response.data && response.data.results) {
        const countriesList = response.data.results.sort((a, b) => a.name.localeCompare(b.name));
        setCountries(countriesList);
        setIsCountriesLoaded(true);
      } else {
        console.error('Cannot get countries list');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getPdfData = async (orderHash, retryCount = 0) => {
    const MAX_RETRIES = 30;
    const RETRY_DELAY = 1250;

    if (retryCount === 0) {
      try {
        await axios.post(`/api/generate_pdf/${orderHash}`);
      } catch (error) {
        console.error('Error initiating PDF generation:', error);
        setCurrentStatus(STATUS_ENUM.orderError);
        return;
      }
    }

    try {
      const response = await axios.get(`/api/generating_pdf_status/${orderHash}`);

      if (response.data.pdf_in_progress) {
        if (retryCount < MAX_RETRIES) {
          setTimeout(() => getPdfData(orderHash, retryCount + 1), RETRY_DELAY);
        } else {
          console.error('PDF generation timeout reached');
          setCurrentStatus(STATUS_ENUM.orderError);
        }
      } else {
        dispatch({ type: 'SET_PDF_URL', payload: response.data.pdf_url });
        // TODO: remove this, when Luva's got same process
        if (requestIsJaguar()) {
          handleAcceptAddToCart();
          //setCurrentStatus(STATUS_ENUM.addToCartPdfAccepting);
        } else {
          setCurrentStatus(STATUS_ENUM.pdfAccepting);
        }
      }
    } catch (error) {
      console.error('Error checking PDF generation status:', error);
      setCurrentStatus(STATUS_ENUM.orderError);
    }
  };

  const getItemData = () => {
    if (!state.colorData.color) {
      alert('Przed dodaniem do koszyka, wybierz kolor');
    }
    const data = {
      product: Number(productID),
      quantity: quantity,
      color: state.colorData.color.color.id,
      config: hash(),
    };
    return data;
  };

  const handleAcceptAddToCart = async () => {
    setCurrentStatus(STATUS_ENUM.orderLoading);
    try {
      const response = await axios.post('/api/cart/', getItemData(), {
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': getCookie('csrftoken') },
      });
      if (response.status === 201) {
        setCurrentStatus(STATUS_ENUM.addToCartGoToCart);
        dispatch({ type: 'SET_IS_DATA_SAVED', payload: true });
      } else {
        setCurrentStatus(STATUS_ENUM.orderError);
        console.error(response.status);
      }
    } catch (error) {
      setCurrentStatus(STATUS_ENUM.orderError);
      console.error(error);
    }
  };

  const handleOrder = () => {
    if (requestIsJaguar()) {
      if (state.isDataSaved) {
        setCurrentStatus(STATUS_ENUM.addToCartPdfLoading);
        getPdfData(hash());
      } else {
        setCurrentStatus(STATUS_ENUM.addToCartSaveOrder);
      }
    }
    setIsOrderModalOpen(true);
  };

  const handleAccept = async () => {
    setCurrentStatus(STATUS_ENUM.orderLoading);
    try {
      const response = await axios.post(`/api/configurator_confirm_pdf/${orderData.order_id}/`);
      if (response.status === 202) {
        setCurrentStatus(STATUS_ENUM.orderSuccess);
      } else {
        setCurrentStatus(STATUS_ENUM.orderError);
        console.error(response.status);
      }
    } catch (error) {
      setCurrentStatus(STATUS_ENUM.orderError);
      console.error(error);
    }
  };

  const handleCloseConfigurator = () => {
    setIsOrderModalOpen(false);
    if (!requestIsJaguar()) {
      localStorage.removeItem(localStorageItemName);
      window.location.reload();
    }
  };

  const handleClose = () => {
    if (currentStatus === STATUS_ENUM.orderSuccess) {
      handleCloseConfigurator();
    } else {
      setIsOrderModalOpen(false);
    }
  };

  const handleSaveClick = () => {
    saveData();
  };

  const handleQuantityChange = e => {
    if (/[0-9]/.test(e.target.value)) {
      if (Number(e.target.value) < minimalOrder) {
        setQuantity(minimalOrder);
      } else if (Number(e.target.value) > 9999) {
        setQuantity(9999);
      } else {
        setQuantity(Number(e.target.value));
      }
    }
  };

  const reduceQuantity = () => {
    if (quantity > minimalOrder) {
      setQuantity(quantity - 1);
    }
  };

  const increaseQuantity = () => {
    if (quantity < 9999) {
      setQuantity(quantity + 1);
    }
  };

  const debounceHandleFormChange = debounce(() => {
    const formData = getValues();
    const payload = {
      ...formData,
      firstName: replaceWithAsterisk(formData.firstName),
      lastName: replaceWithAsterisk(formData.lastName),
      address: replaceWithAsterisk(formData.address),
      addressHouseNumber: replaceAllWithAsterisk(formData.addressHouseNumber),
      addressHouseNumber2: replaceAllWithAsterisk(formData.addressHouseNumber2),
      email: replaceMailWithAsterisk(formData.email),
      phoneNumber: replaceWithAsterisk(formData.phoneNumber),
    };
    setUserData(formData);
    dispatch({ type: 'SET_USER_DATA', payload });
  }, 750);

  const Form = (
    <form
      onSubmit={handleSubmit(data => submitForm(data))}
      onChange={() => debounceHandleFormChange()}
      className="form"
    >
      <div className="form-group form-required w-50">
        <label>{trans('Imię')}:</label>
        <input {...register('firstName', { required: true })} className="form-control" />
        {errors.firstName && <p>{trans('Imię jest wymagane.')}</p>}
      </div>

      <div className="form-group form-required w-50 ml-10">
        <label>{trans('Nazwisko')}:</label>
        <input {...register('lastName', { required: true })} className="form-control" />
        {errors.lastName && <p>{trans('Nazwisko jest wymagane.')}</p>}
      </div>

      <div className="form-group form-required w-50">
        <label>{trans('Ulica')}:</label>
        <input {...register('address', { required: true })} className="form-control" />
        {errors.address && <p>{trans('Ulica jest wymagana.')}</p>}
      </div>

      <div className="form-group form-required w-25 ml-10">
        <label>{trans('Numer domu')}:</label>
        <input {...register('addressHouseNumber', { required: true })} className="form-control" />
        {errors.addressHouseNumber && <p>{trans('Numer domu jest wymagany.')}</p>}
      </div>

      <div className="form-group w-25 ml-10">
        <label>{trans('Numer lokalu')}:</label>
        <input {...register('addressHouseNumber2', { required: false })} className="form-control" />
      </div>

      <div className="form-group form-required w-34">
        <label>{trans('Kod pocztowy')}:</label>
        <input {...register('postalCode', { required: true })} className="form-control" />
        {errors.postalCode && <p>{trans('Kod pocztowy jest wymagany.')}</p>}
      </div>

      <div className="form-group form-required w-66 ml-10">
        <label>{trans('Miejscowość')}:</label>
        <input {...register('city', { required: true })} className="form-control" />
        {errors.city && <p>{trans('Miejscowość jest wymagana.')}</p>}
      </div>

      <div className="form-group form-required w-34">
        <label>{trans('Kraj')}:</label>
        {isCountriesLoaded && (
          <select {...register('country', { required: true })} className="form-control">
            {countries.map(country => (
              <option key={country.id} value={country.code}>
                {country.name}
              </option>
            ))}
          </select>
        )}
        {errors.country && <p>{trans('Wybór kraju jest wymagany.')}</p>}
      </div>

      <div className="form-group w-66 ml-10">
        <label>{trans('Województwo')}:</label>
        <input {...register('province', { required: false })} className="form-control" />
      </div>

      <div className="form-group form-required w-50">
        <label>{trans('Nazwa firmy')}:</label>
        <input {...register('companyName', { required: true })} className="form-control" />
        {errors.companyName && <p>{trans('Nazwa firmy jest wymagana.')}</p>}
      </div>

      <div className="form-group form-required w-50 ml-10">
        <label>{trans('NIP')}:</label>
        <input {...register('taxNumber', { required: true })} className="form-control" />
        {errors.taxNumber && <p>{trans('Numer NIP jest wymagany.')}</p>}
      </div>

      <div className="form-group form-required w-100">
        <label>{trans('Numer telefonu')}:</label>
        <input
          {...register('phoneNumber', {
            required: true,
            pattern: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{3,6}$/im,
          })}
          className="form-control"
        />
        {errors.phoneNumber && errors.phoneNumber.type === 'required' && (
          <p>{trans('Numer telefonu jest wymagany.')}</p>
        )}
        {errors.phoneNumber && errors.phoneNumber.type === 'pattern' && (
          <p>{trans('Podaj poprawny numer telefonu.')}</p>
        )}
      </div>

      <div className="form-group form-required w-100">
        <label>{trans('Adres email')}:</label>
        <input
          {...register('email', { required: true, pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i })}
          className="form-control"
        />
        {errors.email && errors.email.type === 'required' && <p>{trans('Email jest wymagany.')}</p>}
        {errors.email && errors.email.type === 'pattern' && <p>{trans('Podaj poprawny adres email.')}</p>}
      </div>

      <div className="form-group btn-group w-100">
        <button onClick={() => setIsOrderModalOpen(false)} className="btn cancel-btn">
          {trans('Anuluj')}
        </button>
        <button type="submit" className="btn confirm-btn">
          {trans('Potwierdź')}
        </button>
      </div>
    </form>
  );

  const PdfLoader = (
    <div className="w-100">
      <div style={{ margin: '10px 0' }}>
        <i className="fas fa-2xl fa-circle-notch fa-spin"></i>
      </div>
      <div>
        <p>{trans('Proszę czekać trwa generowanie pliku PDF...')}</p>
      </div>
    </div>
  );

  const PdfDownload = (
    <p style={{ margin: '1.75rem 0' }}>
      <i className="fa-solid fa-2x fa-file-pdf" style={{ color: '#d70606', marginBottom: '10px' }}></i>
      <br />
      <a
        onClick={() => setIsPdfDownloaded(true)}
        target="_blank"
        rel="noopener noreferrer"
        href={`${state.pdfUrl}?${Date.now()}`}
        download
      >
        <b>{trans('Kliknij tutaj')}</b>
      </a>{' '}
      {trans('aby pobrać projekt w formacie pdf')}
    </p>
  );

  const PdfAcceptation = (
    <div className="w-100">
      <div>
        <table className="data-table">
          <tbody>
            <tr>
              <td>{trans('Imię i nazwisko')}:</td>
              <td>{`${userData.firstName} ${userData.lastName}`}</td>
            </tr>
            <tr>
              <td>{trans('Nazwa firmy')}:</td>
              <td>{userData.companyName}</td>
            </tr>
            <tr>
              <td>{trans('NIP')}:</td>
              <td>{userData.taxNumber}</td>
            </tr>
            <tr>
              <td>{trans('Adres')}:</td>
              <td>
                {`${userData.address} ${userData.addressHouseNumber}`}
                {userData.addressHouseNumber2 ? `/${userData.addressHouseNumber2}` : ''}
                <br />
                {`${userData.city} ${userData.postalCode} ${userData.country}`}
                {userData.province ? ` (${userData.province})` : ''}
              </td>
            </tr>
            <tr>
              <td>{trans('Numer telefonu')}:</td>
              <td>{userData.phoneNumber}</td>
            </tr>
            <tr>
              <td>{trans('Adres email')}:</td>
              <td>{userData.email}</td>
            </tr>
          </tbody>
        </table>
        {PdfDownload}
        {!isPdfDownloaded && (
          <div className="alert alert-info">
            <p>
              <i className="fa-sharp fa-solid fa-circle-info"></i>{' '}
              {trans('Aby zaakceptować zamówienie pobierz projekt w formacie pdf.')}
            </p>
          </div>
        )}
      </div>
      <div className="form-group btn-group">
        <button onClick={() => handleAccept()} disabled={!isPdfDownloaded} type="button" className="btn confirm-btn">
          <i className="fa-solid fa-check"></i> {trans('Akceptuję i składam zamówienie')}
        </button>
      </div>
    </div>
  );

  const OrderLoader = (
    <div className="w-100">
      <div style={{ margin: '10px 0' }}>
        <i className="fas fa-2xl fa-circle-notch fa-spin"></i>
      </div>
      <div>
        <p>{trans('Proszę czekać trwa składanie zamówienia...')}</p>
      </div>
    </div>
  );

  const SuccessMsg = (
    <div className="w-100">
      <div>
        <p>
          {trans('Dziękujemy za złożenie zamówienia')} {`${userData.firstName} ${userData.lastName}`}!
        </p>
        <p>
          {trans('Twój numer zamówienia to')}: {orderData.order_id}
        </p>
        {PdfDownload}
        <p>{trans('Skontaktujemy się z Tobą najszybciej jak to możliwe.')}</p>
      </div>
      <div className="form-group btn-group">
        <button onClick={() => handleCloseConfigurator()} type="button" className="btn confirm-btn">
          OK
        </button>
      </div>
    </div>
  );

  const ErrorMsg = (
    <div className="w-100">
      <div>
        <p>{trans('Przepraszamy!')}</p>
        <p>
          {trans('Wystąpił błąd podczas składania zamówienia, prosimy o ponownę próbę lub kontakt z administratorem.')}
        </p>
      </div>
      <div className="form-group btn-group">
        <button onClick={() => setIsOrderModalOpen(false)} type="button" className="btn confirm-btn">
          OK
        </button>
      </div>
    </div>
  );

  const AddToCartPdfAcceptation = (
    <div className="w-100">
      <div>
        {PdfDownload}
        {!isPdfDownloaded && (
          <div className="alert alert-info">
            <p>
              <i className="fa-sharp fa-solid fa-circle-info"></i>{' '}
              {trans('Aby zaakceptować zamówienie pobierz projekt w formacie pdf.')}
            </p>
          </div>
        )}
      </div>
      <div className="form-group btn-group">
        <button
          onClick={() => handleAcceptAddToCart()}
          disabled={!isPdfDownloaded}
          type="button"
          className="btn confirm-btn"
        >
          <i className="fa-solid fa-check"></i> {trans('Akceptuję i dodaje do koszyka')}
        </button>
      </div>
    </div>
  );

  const AddToCartLoader = (
    <div className="w-100">
      <div style={{ margin: '10px 0' }}>
        <i className="fas fa-2xl fa-circle-notch fa-spin"></i>
      </div>
      <div>
        <p>{trans('Proszę czekać trwa dodawanie do koszyka...')}</p>
      </div>
    </div>
  );

  const AddToCartSaveOrder = (
    <div className="w-100">
      <div>
        {trans('Przed dodaniem do koszyka należy zapisać projekt')}
        <br />
        {trans('Czy chcesz teraz zapisać?')}
      </div>
      <div className="modal__actions">
        <button className="btn confirm-btn" onClick={handleSaveClick}>
          {trans('Tak, zapisz')}
        </button>
        <button className="btn cancel-btn" onClick={handleClose}>
          {trans('Nie, opuść')}
        </button>
      </div>
    </div>
  );

  const AddToCartGoToCart = (
    <div className="w-100">
      <div>
        {trans('Dodano produkt do koszyka, co chcesz teraz zrobić?')}
      </div>
      <div className="modal__actions">
        <button className="btn confirm-btn" onClick={redirectToCart}>
          {trans('Idź do koszyka')}
        </button>
        <button className="btn cancel-btn" onClick={handleCloseConfigurator}>
          {trans('Kontynuuj zamawianie')}
        </button>
      </div>
    </div>
  );

  const ORDER_FORM_STAGE_MAP = {
    [STATUS_ENUM.form]: Form,
    [STATUS_ENUM.pdfLoading]: PdfLoader,
    [STATUS_ENUM.pdfAccepting]: PdfAcceptation,
    [STATUS_ENUM.orderLoading]: OrderLoader,
    [STATUS_ENUM.orderSuccess]: SuccessMsg,
    [STATUS_ENUM.orderError]: ErrorMsg,
    [STATUS_ENUM.addToCartPdfLoading]: PdfLoader,
    [STATUS_ENUM.addToCartPdfAccepting]: AddToCartPdfAcceptation,
    [STATUS_ENUM.addToCartLoading]: AddToCartLoader,
    [STATUS_ENUM.addToCartSaveOrder]: AddToCartSaveOrder,
    [STATUS_ENUM.addToCartGoToCart]: AddToCartGoToCart,
  };

  useEffect(() => {
    getCountriesList();
  }, []);

  useEffect(() => {
    if (isOrderModalOpen === false) {
      setCurrentStatus(STATUS_ENUM.form);
    }
  }, [isOrderModalOpen]);

  return (
    <>
      <div style={{ display: 'flex' }}>
        <div className="quantity-container">
          <span>{trans('Liczba sztuk')}:</span>
          <div className="quantity-input">
            <button
              onClick={() => reduceQuantity()}
              type="button"
              className="quantity-btn"
              disabled={quantity <= minimalOrder}
              title={quantity <= minimalOrder ? `${trans('Minimalne zamówienie wynosi')}: ${minimalOrder}` : ''}
            >
              <i className="fa fa-caret-down" aria-hidden="true"></i>
            </button>
            <input type="number" value={quantity} onChange={e => handleQuantityChange(e)} />
            <button onClick={() => increaseQuantity()} type="button" className="quantity-btn">
              <i className="fa fa-caret-up" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        <button className="btn confirm-btn order-btn" onClick={handleOrder}>
          {requestIsJaguar() ? trans('Dodaj do koszyka') : trans('Zamów')}
        </button>
      </div>

      {isOrderModalOpen && (
        <Modal handleClose={() => handleClose()} title={requestIsJaguar() ? trans('Dodaj do koszyka') : trans('Zamów')}>
          {ORDER_FORM_STAGE_MAP[currentStatus]}
        </Modal>
      )}
    </>
  );
};
